import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { currentOrigin, withReturnTo } from '../../pricingJourney/navigation';

import {
  Box,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import CommercialQueryPanel from './CommercialQueryPanel';

import type {
  MarginCenterCommercialQueryContext,
  MarginCenterCustomerProductSelection,
  MarginCenterCustomerProductSeriesPoint,
} from '../types/marginCenter';

// El origen permanece visible. Solo uno de los dos ámbitos se envía en cada
// consulta: fijar ambos convertiría el resultado en la propia relación.
type InquiryScope = 'customer' | 'product';

export default function RelationshipCommercialQueryPanel({
  customerId,
  productId,
  customerLabel,
  productLabel,
  selection,
  series,
  currencyCode,
}: {
  customerId: string;
  productId: string;
  customerLabel: string;
  productLabel: string;
  selection: MarginCenterCustomerProductSelection;
  series: MarginCenterCustomerProductSeriesPoint[];
  currencyCode: string | null;
}) {
  const [scope, setScope] = useState<InquiryScope>('customer');
  const navigate = useNavigate();
  const location = useLocation();

  const context = useMemo<MarginCenterCommercialQueryContext>(() => ({
    start_date: selection.start_date ?? undefined,
    end_date: selection.end_date ?? undefined,
    comparison_mode: selection.comparison_mode,
    comparison_start_date: selection.comparison_mode === 'custom'
      ? selection.comparison_start_date ?? undefined : undefined,
    comparison_end_date: selection.comparison_mode === 'custom'
      ? selection.comparison_end_date ?? undefined : undefined,
    time_grain: selection.time_grain,
    customer_id: scope === 'customer' ? customerId : undefined,
    product_id: scope === 'product' ? productId : undefined,
  }), [selection, scope, customerId, productId]);

  const navigationParams = () => {
    const params = new URLSearchParams();
    if (selection.start_date) params.set('from', selection.start_date);
    if (selection.end_date) params.set('to', selection.end_date);
    params.set('comparison', selection.comparison_mode);
    if (selection.comparison_mode === 'custom') {
      if (selection.comparison_start_date) params.set('comparisonFrom', selection.comparison_start_date);
      if (selection.comparison_end_date) params.set('comparisonTo', selection.comparison_end_date);
    }
    params.set('grain', selection.time_grain);
    return params;
  };

  const openCustomer = (nextCustomerId: string) => {
    const params = navigationParams();
    // PRC-027: la ficha vuelve a la pantalla desde la que se abrió.
    navigate(withReturnTo(`/margin-center/customers/${encodeURIComponent(nextCustomerId)}?${params.toString()}`, currentOrigin(location)));
  };

  const openRelationship = (nextCustomerId: string, nextProductId: string) => {
    const params = navigationParams();
    params.set('dimension', 'customer_product');
    params.set('relationshipCustomer', nextCustomerId);
    params.set('relationshipProduct', nextProductId);
    navigate(`/margin-center/explorer?${params.toString()}`);
  };

  return (
    <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Paper elevation={0} sx={{ p: 1.1, borderRadius: 2, bgcolor: 'rgba(15,23,42,0.46)', border: '1px solid rgba(148,163,184,0.16)' }}>
        <Typography variant="subtitle2" sx={{ color: '#f8fafc', fontWeight: 850 }}>
          Explorar el entorno comercial
        </Typography>
        <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mt: 0.2, lineHeight: 1.4 }}>
          Origen: {customerLabel} × {productLabel}
        </Typography>
        <ToggleButtonGroup
          exclusive
          fullWidth
          size="small"
          value={scope}
          onChange={(_, next: InquiryScope | null) => { if (next) setScope(next); }}
          sx={{ mt: 1, '& .MuiToggleButton-root': { minWidth: 0, px: 0.6, fontSize: '0.72rem', textTransform: 'none', color: '#94a3b8', borderColor: 'rgba(148,163,184,0.22)', '&.Mui-selected': { color: '#f8fafc', bgcolor: 'rgba(56,189,248,0.14)' } } }}
          aria-label="Ámbito de la consulta comercial"
        >
          <ToggleButton value="customer">Productos del cliente</ToggleButton>
          <ToggleButton value="product">Clientes del producto</ToggleButton>
        </ToggleButtonGroup>
      </Paper>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <CommercialQueryPanel
          key={scope}
          context={context}
          currencyCode={currencyCode}
          questionTitle="¿Qué quieres investigar?"
          hasPartialBucket={series.some((point) => point.is_partial_bucket)}
          scopeLabel={scope === 'customer'
            ? `Productos del cliente · ${customerLabel}`
            : `Clientes del producto · ${productLabel}`}
          suggestedQuestions={scope === 'customer'
            ? [
              '¿Qué productos de este cliente tienen más ingresos?',
              '¿En qué productos cae el margen porcentual?',
              '¿Dónde sube el coste unitario y baja el margen?',
            ]
            : [
              '¿Qué clientes compran más este producto?',
              '¿En qué clientes cae el margen porcentual?',
              '¿Dónde sube el precio y bajan las unidades?',
            ]}
          onOpenCustomer={openCustomer}
          onOpenRelationship={openRelationship}
        />
      </Box>
    </Box>
  );
}
